import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { TrackingHandler } from 'client/tracking/handler';
import { CommonMap } from 'client/tracking/maps/common';
import { IMAGE_SIZES } from 'site-modules/shared/constants/editorial/editorial';
import { getFullImageUrl, getStaticImageUrl } from 'client/utils/image-helpers';
import { EmbeddedYoutube } from 'site-modules/shared/components/video/embedded-youtube/embedded-youtube';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { Link } from 'site-modules/shared/components/link/link';
import { BrandModuleCards } from 'site-modules/shared/components/brand-module-cards/brand-module-cards';

import './index.scss';

const EXPERTS_PHOTO_URL = 'dam:///photo/non-make/ot/ot_506212';
const SECTION_CLASS_NAME = 'about-section mx-auto px-0_5';
const EVERY_YEAR_POINTS = [
  { icon: 'icon-meter', action: 'drives', amount: 'over 1/2 million', measure: 'miles' },
  { icon: 'icon-clipboard2', action: 'conducts', amount: 'thousands', measure: 'of hours of testing' },
  { icon: 'icon-steering-wheel', action: 'on', amount: 'more than 300', measure: 'new vehicles' },
];
const TRUST_POINTS = [
  { icon: 'icon-key', label: 'Transparency' },
  { icon: 'icon-tool6', label: 'Resourcefulness' },
  { icon: 'icon-right25', label: 'Urgency' },
  { icon: 'icon-cog2', label: 'Simplicity' },
  { icon: 'icon-users4', label: 'Togetherness' },
];
const OUR_LINKS = [
  {
    icon: 'icon-user-tie1',
    label: 'Our executives',
    description: 'Meet our executive leadership team.',
    to: '/about/executive-bios.html',
  },
  {
    icon: 'icon-newspaper2',
    label: 'Our newsroom',
    description: 'See our latest press releases and media contact information.',
    to: '/industry/press/',
  },
];
const TEXT_LINKS = {
  VIDEO_REVIEWS: 'https://www.youtube.com/playlist?list=PLF30645FEF20FA600',
  APPRAISALS: '/appraisal/',
  VERIFY_PRICE: '/price-checker/',
  TOP_RATED: '/top-rated/',
  HOW_TEST: '/car-reviews/features/how-we-test-cars-and-trucks.html',
  EDITORIAL_TEAM: '/about/editors.html',
  ARTICLES: '/car-reviews/car-reviews-road-tests.html',
  VIDEOS: '/car-videos/',
  CALCULATORS: '/calculators/',
  CAREERS: '/careers/',
};

function AboutUI({ isMobile = false }) {
  useEffect(() => {
    TrackingHandler.useMap(CommonMap);
  }, []);

  return (
    <main id="main-content" tabIndex={-1} className="about pb-3_5 mb-3_5">
      <div className="hero-container pos-r mb-2 mb-md-0">
        <img
          className="hero-image h-100 w-100"
          src={getStaticImageUrl(
            isMobile
              ? '/img/press/office-pictures/pro/cadillacfront-large.jpg'
              : '/img/press/office-pictures/pro/cadillacfront-xl.jpg'
          )}
          alt="Edmunds"
        />
        <div className="gradient w-100 pos-a bottom-0 d-flex align-items-end">
          <Container tag="h1" className="heading-2 text-white mb-0 px-0_5 pb-1_5 pb-md-2 ms-0 ms-md-auto">
            About Us
          </Container>
        </div>
      </div>
      <div className={classnames('pt-md-2_5 pb-md-1_5 mb-3', { 'bg-brand-primary-opacity': !isMobile })}>
        <BrandModuleCards
          isMobile={isMobile}
          className="about-extra-wide-section mx-auto px-0_5"
          headingClassName={classnames('text-center', {
            'heading-1 fw-bold': !isMobile,
            'heading-3': isMobile,
          })}
          subHeadingClassName="text-center mb-md-3"
          dataTrackingParent="edm-entry-brand-module"
        />
      </div>
      <div className={classnames(SECTION_CLASS_NAME, 'mb-3_5')}>
        <div className="tagline-container bg-brand-primary-opacity px-1 py-2 d-flex justify-content-center">
          <div
            className={classnames('tagline text-center fw-medium', {
              'heading-2': !isMobile,
              'heading-3': isMobile,
            })}
          >
            We take the guesswork out of buying or selling a vehicle.
          </div>
        </div>
      </div>
      <Row className={classnames(SECTION_CLASS_NAME, 'mb-1_5')}>
        <Col xs={12} md={6} className="text-gray-darker ps-md-0 mb-1_5 mb-md-0">
          <h3 className="heading-4 mb-0_5">Who we are</h3>
          With over 50 years of experience, Edmunds is the trusted guide in car shopping. Our website has in-depth{' '}
          <Link className="text-primary-darker" to={TEXT_LINKS.VIDEO_REVIEWS} target="_blank">
            video reviews
          </Link>
          , new and used listings, free{' '}
          <Link className="text-primary-darker" to={TEXT_LINKS.APPRAISALS}>
            appraisals
          </Link>
          , and calculators that{' '}
          <Link className="text-primary-darker" to={TEXT_LINKS.VERIFY_PRICE}>
            verify a good price
          </Link>
          . Shoppers count on us at every step of their journey.
        </Col>
        <Col xs={12} md={6} className="pe-md-0">
          <EmbeddedYoutube creativeId="who-we-are-video" videoTitle="Edmunds.com" videoId="hjRuHxLBB9A" />
        </Col>
      </Row>
      <div className="bg-gray-extra-light pt-2 pb-3_5 mb-2">
        <Row className={classnames(SECTION_CLASS_NAME, 'mb-3 mb-md-3_5 flex-md-row-reverse')}>
          <Col xs={12} md={6} className="text-gray-darker pe-md-0 mb-1_5 mb-md-0">
            <h3 className="heading-4 mb-0_5">We&apos;re the car experts</h3>
            We literally get in the driver&apos;s seat to show shoppers how each car performs on the road. Read more
            about our annual{' '}
            <Link className="text-primary-darker" to={TEXT_LINKS.TOP_RATED}>
              Top Rated Awards
            </Link>{' '}
            and{' '}
            <Link className="text-primary-darker" to={TEXT_LINKS.HOW_TEST}>
              how our editors test cars
            </Link>
            .
          </Col>
          <Col xs={12} md={6} className="ps-md-0">
            <AspectRatioContainer aspectRatio="2:1">
              <img
                className="expert-photo"
                src={getFullImageUrl(EXPERTS_PHOTO_URL, IMAGE_SIZES.md)}
                alt="Edmunds experts"
              />
            </AspectRatioContainer>
          </Col>
        </Row>
        <Row className={classnames(SECTION_CLASS_NAME, 'text-primary-darker fw-medium text-center')}>
          <Col tag="h3" xs={12} className="size-24 fw-medium mb-md-2">
            Every year, our editorial team…
          </Col>
          {EVERY_YEAR_POINTS.map(({ icon, action, amount, measure }) => (
            <Col key={action} xs={12} md={4} className="mt-2_5 mt-md-0">
              {action}
              <i className={classnames(icon, 'every-year-icon d-block size-36 mt-0_5 mb-1')} />
              <div className="size-30 mb-0_25">{amount}</div>
              <div className="size-20 fw-normal">{measure}</div>
            </Col>
          ))}
        </Row>
      </div>
      <div className={classnames(SECTION_CLASS_NAME, 'text-gray-darker mb-3_5')}>
        <h3 className="heading-4 mb-0_5">We know car shopping</h3>
        <div className="mb-2">
          Car shopping can be complicated. That&apos;s why the{' '}
          <Link className="text-primary-darker" to={TEXT_LINKS.EDITORIAL_TEAM}>
            Editorial Team
          </Link>{' '}
          at Edmunds empowers shoppers with advice through our{' '}
          <Link className="text-primary-darker" to={TEXT_LINKS.ARTICLES}>
            articles
          </Link>{' '}
          and{' '}
          <Link className="text-primary-darker" to={TEXT_LINKS.VIDEOS}>
            videos
          </Link>
          . We tackle all the important questions. Should you buy new or used? What about leasing? We provide answers to
          help shoppers navigate the process.
        </div>
        <h3 className="heading-4 mb-0_5">We help shoppers buy with confidence</h3>
        <div>
          Shoppers should feel good about their purchase. That&apos;s why we let them know if they&apos;re getting the
          right deal — we provide deal ratings on nearby listings, quote comparisons, and{' '}
          <Link className="text-primary-darker" to={TEXT_LINKS.CALCULATORS}>
            calculators
          </Link>{' '}
          to determine budget and financing. We also help them find home delivery for a safer experience.
        </div>
      </div>
      <div className="bg-brand-primary-opacity pt-2 pb-3_5 pt-md-3_5 mb-3_5">
        <div className={classnames(SECTION_CLASS_NAME, 'mb-2 text-gray-darker')}>
          <h2 className="heading-3 mb-0_5">Our company culture</h2>
          Our employees are the key to our continued success and innovation. If you join our team, you&apos;ll become
          part of our culture of TRUST.
        </div>
        <div className="about-wide-section mx-auto px-0_5 text-center">
          <h3 className="heading-3 mb-md-2_5">TRUST</h3>
          <ul className="trust-list text-gray-darker text-center list-unstyled d-flex flex-column flex-md-row justify-content-between mb-3_5">
            {TRUST_POINTS.map(({ icon, label }) => (
              <li key={label} className="trust-item size-20 mt-2_5 mt-md-0">
                <div className="icon-container bg-primary mx-auto mb-1 pos-r">
                  <i className={classnames(icon, 'trust-icon pos-a center-xy text-white')} />
                </div>
                {label}
              </li>
            ))}
          </ul>
        </div>
        <div className={classnames(SECTION_CLASS_NAME, 'text-gray-darker')}>
          These are our core values that drive the way we work. In order to fuel the potential of our company and
          employees, we also focus on maintaining an inclusive work environment filled with diverse perspectives,
          experiences and backgrounds. We support the ongoing development and well-being of our employees and believe
          that by giving tools, resources and TRUST to employees, incredible results are possible.
          <div>
            <Link className="text-primary-darker" to={TEXT_LINKS.CAREERS}>
              Learn more about careers at Edmunds
              <i className="icon-arrow-right3 d-inline-block ms-0_25 size-10" aria-hidden />
            </Link>
          </div>
        </div>
      </div>
      <div className={classnames(SECTION_CLASS_NAME, 'mb-1_5')}>
        {OUR_LINKS.map(({ icon, label, description, to }) => (
          <Link
            key={icon}
            to={to}
            className="our-link rounded text-decoration-none text-gray-darker p-1 px-md-1_5 py-md-2 mb-1_5 d-flex align-items-center justify-content-between"
          >
            <div className="ps-0 d-flex align-items-center">
              <i
                className={classnames(
                  'link-icon d-flex align-items-center justify-content-center text-primary me-1 me-md-1_5 size-20',
                  icon
                )}
              />
              <div className="d-inline-block pe-0_75">
                <div className="label-text fw-bold mb-0_25">{label}</div>
                {description}
              </div>
            </div>
            <div className="px-0 d-flex justify-content-end justify-content-md-between align-items-center">
              <i className="icon-arrow-right3 text-primary-darker medium" aria-hidden />
            </div>
          </Link>
        ))}
      </div>
    </main>
  );
}

AboutUI.propTypes = {
  isMobile: PropTypes.bool,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const About = connect(mapStateToProps)(AboutUI);
